import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { Button } from '~/eds/src/components/Button/Button';
import { EsignatureType } from '~/enums/EsignatureType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { api } from '~/redux/api/createApi';
import * as selectors from '~/redux/selectors';
import { Nullable } from '~/types/Nullable';
import { shouldFillFormQuestionsInSignStageEsignatureRevamp } from '~/utils/ticket';

import { EsignatureEnvelopeStatusValue } from '../types/esignature';
import { useHandleEsignAuthenticateAction } from './useHandleEsignAuthenticateAction';
import { useSendToEsignatureTooltip } from './useSendToEsignatureTooltip';

export const SendToEsignature = () => {
  const intl = useIntl();
  const { toast } = useErrorToast();

  const ticket = useSelector(selectors.selectTicketState);
  const { id } = ticket;
  const signStatus: Nullable<TicketStatusType> = ticket.stages.sign?.status;
  const { data: esignatureTokenStatus, isLoading: isLoadingTokenStatus } =
    api.endpoints.getTokenStatus.useQuery(
      {
        ticketId: id,
      },
      {
        skip: !id,
      },
    );
  const { data: recipients, isLoading: isLoadingRecipients } =
    api.endpoints.getRecipients.useQuery(
      {
        ticketId: ticket.id,
      },
      {
        skip: !ticket.id,
      },
    );
  const tooltip = useSendToEsignatureTooltip({
    esignatureTokenStatus,
    isLoadingTokenStatus,
    signers: recipients?.signers ?? [],
  });
  const {
    handleEsignAuthenticateAction,
    isLoading: isLoadingEsignAuthenticate,
  } = useHandleEsignAuthenticateAction();
  const hasEsignatureIntegration = !!esignatureTokenStatus;
  const esignatureProvider = esignatureTokenStatus?.providerType;
  const { data: envelope, isLoading: isLoadingEnvelope } =
    api.endpoints.getEnvelope.useQueryState(
      {
        ticketId: id,
      },
      {
        skip: !id || !hasEsignatureIntegration,
      },
    );
  const hasValidEnvelope =
    !!envelope &&
    envelope.status.value !== EsignatureEnvelopeStatusValue.Error &&
    envelope.status.value !== EsignatureEnvelopeStatusValue.Declined &&
    envelope.status.value !== EsignatureEnvelopeStatusValue.Voided;
  const envelopeStatus = envelope?.status.value;
  const isNoSigners = recipients?.signers?.length === 0;
  const [createEnvelope, { isLoading }] =
    api.endpoints.createEnvelope.useLazyQuery();

  if (
    // check on the ticket from everest and from esign provider incase the ticket was signed via Upload
    signStatus === TicketStatusType.Signed ||
    envelopeStatus === EsignatureEnvelopeStatusValue.Signed ||
    hasValidEnvelope ||
    isNoSigners ||
    shouldFillFormQuestionsInSignStageEsignatureRevamp(ticket, envelopeStatus)
  ) {
    return null;
  }

  const handleCreateEnvelope = async () => {
    const newTab = window.open('', '_blank');
    try {
      const result = await createEnvelope({
        ticketId: id,
        returnUrl: window.location.href,
        // getTooltip() will disable the button if there is no esignatureProvider, so we know here it will always be defined
        vendor: esignatureProvider as EsignatureType,
      }).unwrap();
      if (result?.redirectUrl && newTab) {
        newTab.location.href = result.redirectUrl;
      }
    } catch (error) {
      if (newTab) {
        newTab.close();
      }

      if (error instanceof Error) {
        toast(error, { autoDismiss: false });
      }
    }
  };

  return (
    <Button
      text={intl.formatMessage({
        id: 'SendToEsignature.sendToEsignatureButton',
        defaultMessage: 'Send to E-Signature',
        description:
          'Button in the ticket sign stage that will link to a e-signature provider to create a signing envelope',
      })}
      isLoading={isLoading}
      variant="primary"
      tooltip={tooltip}
      disabled={
        Boolean(tooltip) ||
        isLoadingTokenStatus ||
        isLoadingEnvelope ||
        isLoadingEsignAuthenticate ||
        isLoadingRecipients
      }
      onClick={() => handleEsignAuthenticateAction(handleCreateEnvelope)}
    />
  );
};
